import { useEffect, useRef, useState } from 'react';
import { fetchMatrixSlice, type MatrixSlice, type Reference } from '../api.ts';
import MatrixPanel from './MatrixPanel.tsx';

/**
 * Where a claim came from.
 *
 * EVERY ACCOUNT GETS THE PAGE, from 2026-09-08. Misha: "for both superuser and 'demo'
 * user and regular user, they should be able to see the matrix PDF stuff." The panel
 * renders the lender's own page from the lender's own PDF, with the quote boxed on it,
 * for whoever is signed in.
 *
 * It used to stop at the attribution for a broker - lender, document, section, page and
 * the sentence - on the argument that the page is the lender's document rather than our
 * claim about it, and that opening it is an audit instrument for us. What that got wrong
 * is who the audit is for. A broker told "Arc caps you at 80% here" has no way to tell a
 * correct retrieval from a wrong one, and the product's whole claim is that the answer
 * rests on the lender's page - a claim only the page can settle.
 *
 * TWO FLAGS NOW, AND THEY ARE NOT THE SAME QUESTION. `sourceViewable` is the lender's
 * material: the page image and, in MatrixPanel, the verbatim footnotes. It comes from
 * `canOpenSourceDocuments`, the same function the API preHandler asks, so this decides
 * what is DRAWN and never what is permitted. `staffView` is OUR bookkeeping that happens
 * to live in this panel - how old our copy of the document is, and whether the grid route
 * answered - and comes from `showsInternalSweep`. They were one boolean while they
 * happened to agree; splitting them is what stopped the age badge following the page.
 *
 * The engine already refuses to emit a quote that is not a literal substring of the
 * stored page text, but that guarantee lives in a server-side validator and a database
 * constraint - neither of which anyone can inspect from a browser. The page is where the
 * promise becomes visible.
 *
 * Two states matter as much as the happy path.
 *
 * A citation with no bounding box is honest about it rather than boxing a guess. Roughly
 * two thirds of citations are in this state today: vision-extracted snippets normalize
 * whitespace differently from the text layer, so the quote often cannot be located to a
 * pixel. The quote is still exact and the page is still right - only the box is unknown,
 * and drawing one in the wrong place would be worse than drawing none.
 *
 * A page that fails to load says so in words. A broken-image icon inside a citation
 * panel reads as "the reference is fabricated", which is the one impression this product
 * cannot afford to give by accident.
 */

interface Props {
  reference: Reference;
  /**
   * Whether this account may open the source document. Comes from
   * `canOpenSourceDocuments` in @lenderhound/shared - the same function the API's
   * preHandler asks - so the panel and the route cannot disagree about who gets a page.
   */
  sourceViewable: boolean;
  /**
   * Whether to draw OUR notes about the document, as opposed to the document. From
   * `showsInternalSweep` - the age of our copy and the grid-route diagnostic, neither of
   * which is a fact about the lender and neither of which a broker can act on.
   */
  staffView: boolean;
  onClose: () => void;
}

type PageState = 'loading' | 'ready' | 'failed';

/**
 * Whether the grid lookup has run, and what came back.
 *
 * `absent` is not a failure and is not `failed`. It is the answer for a citation that
 * points at a paragraph rather than a cell, which is the common case across the corpus:
 * 28,482 of 37,620 citations have no cell behind them. (It was also the answer for EVERY
 * citation while the grid route was unshipped; that route is deployed now, so an `absent`
 * here means the citation, not the API.) It renders as nothing at all except for staff,
 * because the panel above is already complete without a grid.
 */
type SliceState = 'idle' | 'loading' | 'ready' | 'absent' | 'failed';

/**
 * Slices, held for the life of the page.
 *
 * A citation's cells are immutable - re-ingesting a document produces a new version with
 * new citation rows, and therefore new ids - which is the same argument the API makes for
 * caching a rendered page forever. What makes it worth doing here rather than leaving to
 * HTTP is the access pattern the citation chip creates: open a chip, close it, open the
 * one below, come back to the first. That is three fetches of the same slice across a
 * thirty-second stretch, over a database sitting behind an SSH tunnel.
 */
const sliceCache = new Map<string, MatrixSlice>();

/**
 * doc_kind said the way a broker would say it, for the attribution line.
 *
 * Falls back to "document" rather than printing the enum. `overlay_letter` on screen is
 * the tell that a raw column value reached the page, and it is the kind of detail that
 * makes a careful reader distrust the rest of the panel.
 */
const DOCUMENT_NOUN: Record<string, string> = {
  matrix: 'rate matrix',
  guideline: 'guideline',
  rate_sheet: 'rate sheet',
  overlay_letter: 'overlay letter',
};

/** Mirrors STALENESS_DAYS in the ingest package: grids move monthly, prose annually. */
const STALE_AFTER_DAYS: Record<string, number> = { matrix: 90, rate_sheet: 30 };
const DEFAULT_STALE_AFTER_DAYS = 400;

/**
 * How old our copy of the document is. OURS ONLY - see the gate at the call site.
 *
 * A quoted sentence carries no date. Deephaven's business-purpose guide is effective
 * November 2023 and reads exactly like a current one once a paragraph is lifted out of
 * it, so knowing the age of the page a claim came off is real information - to us. It is
 * what tells whoever is checking an answer that a number needs re-reading against a
 * current issue, and what sends somebody to go and re-ingest.
 *
 * Deliberately silent when the document states no effective date: "unknown age" invites
 * a guess, and an absent badge is what the rest of this component already does with an
 * absent bounding box.
 */
function DocumentAge(
  { kind, effectiveDate, ageDays }:
  { kind: string; effectiveDate: string | null; ageDays: number | null },
) {
  if (effectiveDate == null || ageDays == null) return null;
  const stale = ageDays > (STALE_AFTER_DAYS[kind] ?? DEFAULT_STALE_AFTER_DAYS);
  const years = ageDays / 365;
  const age = years >= 1 ? `${years.toFixed(1)} yr old` : `${ageDays}d old`;
  return (
    <div style={{ fontSize: 12, marginTop: 6, color: stale ? 'var(--warn, #b45309)' : 'var(--muted)' }}>
      {stale ? '⚠ ' : ''}
      Effective {effectiveDate.slice(0, 10)} · {age}
      {stale ? ' · confirm with the AE before papering a file' : ''}
    </div>
  );
}

export default function CitationViewer({
  reference, sourceViewable, staffView, onClose,
}: Props) {
  const [page, setPage] = useState<PageState>('loading');
  const [zoom, setZoom] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const bbox = reference.bbox_norm;
  const hasBox = Array.isArray(bbox) && bbox.length === 4;
  const src = `/api/citation/${reference.id}/page.png`;

  const [slice, setSlice] = useState<MatrixSlice | null>(null);
  const [sliceState, setSliceState] = useState<SliceState>('idle');

  /*
    Only a matrix citation has a grid behind it.

    Gated on the document kind rather than tried for everything and allowed to 404,
    because the two are not the same request: every guideline citation in an answer would
    otherwise fire a lookup that is guaranteed to come back empty, and a synthesis run
    routinely produces a dozen of them.

    The whole effect re-runs on the citation id, not just on mount. A chip clicked while
    this panel is already open replaces `reference` in place without unmounting - see
    onCite in Results.tsx - so state that only initialised once would show the previous
    citation's grid under the new citation's quote, which is the single worst thing this
    component could get wrong.
  */
  const isMatrix = reference.document_kind === 'matrix';
  useEffect(() => {
    if (!isMatrix) { setSlice(null); setSliceState('idle'); return; }
    const cached = sliceCache.get(reference.id);
    if (cached) { setSlice(cached); setSliceState('ready'); return; }

    setSlice(null);
    setSliceState('loading');
    const ac = new AbortController();
    let live = true;
    fetchMatrixSlice(reference.id, ac.signal)
      .then((got) => {
        if (!live) return;
        if (!got) { setSliceState('absent'); return; }
        sliceCache.set(reference.id, got);
        setSlice(got);
        setSliceState('ready');
      })
      .catch(() => { if (live) setSliceState('failed'); });
    return () => { live = false; ac.abort(); };
  }, [isMatrix, reference.id]);

  // Escape closes, and focus moves into the dialog so the keyboard works at all.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A guideline page is tall and the quote is usually not at the top of it. Scrolling
  // the highlight into view is the difference between "here is the page" and "here is
  // the sentence".
  useEffect(() => {
    if (!sourceViewable || page !== 'ready' || !hasBox) return;
    boxRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [sourceViewable, page, hasBox]);

  const pct = (n: number): string => `${(n * 100).toFixed(3)}%`;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        zIndex: 50,
      }}
    >
      <div
        ref={dialogRef}
        className="card"
        role="dialog"
        aria-modal="true"
        aria-label={`Citation from ${reference.lender_name}`}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          // Wider once there is a grid in it, and only then. A FICO ladder against four
          // loan bands does not fit 880px without scrolling sideways for the first
          // column, and a one-sentence guideline quote in a 1040px dialog is a lot of
          // white space around three lines of text.
          maxWidth: slice?.slice ? 1040 : 880,
          width: '100%', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', gap: 12, outline: 'none',
        }}
      >
        <div>
          <div className="lbl">{reference.lender_name} · {reference.document_kind}</div>
          <div className="serif" style={{ fontSize: 18, marginBottom: 4 }}>
            {reference.document_title}
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            {reference.section_number ? `§${reference.section_number} · ` : ''}
            {reference.section_title ?? ''}
            {' · page '}{reference.printed_label ?? reference.page_index}
          </div>
          {/*
            The age line stops at the broker.

            "This matrix is 152 days old - confirm with the AE before papering a file"
            reads as a warning about the lender and is a confession about us. A broker
            cannot act on it: they did not choose the document, they cannot fetch a newer
            one, and the only move it leaves them is to distrust the answer they came for.
            The staleness of our corpus is our problem to fix, on our side, which is
            exactly where the badge still renders.

            It hangs on `staffView` rather than `sourceViewable` since 2026-09-08. The two
            were one boolean, and when the page was opened to every account this badge
            followed it out - putting "152d old" in front of the reader it was reasoned
            away from. The page is the lender's document; the age is our note about the
            shelf it came off, and only one of those changed hands.
          */}
          {staffView ? (
            <DocumentAge
              kind={reference.document_kind}
              effectiveDate={reference.effective_date ?? null}
              ageDays={reference.age_days ?? null}
            />
          ) : null}
        </div>

        <blockquote style={{
          margin: 0, padding: '12px 16px', background: 'var(--blue-soft)',
          borderLeft: '3px solid var(--blue)', borderRadius: 6,
          fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap',
        }}>
          {reference.quote}
        </blockquote>

        {/*
          The evidence under the quote, in one scrolling column.

          Order is an argument, not a layout: the grid the cited number sits in, then the
          lender's prose that qualifies that grid, then - for staff - the rendered page
          the whole thing was read off. It runs from our reading of the document to the
          document itself, so each element is the check on the one above it.

          One vertical scroller for the lot rather than a scroller per element. Three
          independently scrolling boxes stacked inside a modal is how a reader loses the
          footnote entirely: it sits below the fold of a box whose fold nothing indicates.
        */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          // Never sideways. Wide content owns its own overflow-x - see .mtx-scroll and
          // the page box below - so the dialog itself has nothing to scroll horizontally
          // and neither does the document body behind it.
          overflowX: 'hidden',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          {/*
            The grid, and the fine print printed beside it. Henry, 2026-09-01: "I think we
            show both the guideline paragraph + the whole matrix, this will be very
            impressive." The account boundary inside it - cells for everyone, the lender's
            verbatim paragraphs for staff - is argued at the top of MatrixPanel.tsx.

            Every state but `ready` renders nothing here. A grid is an enhancement to a
            citation that is already complete; a spinner or an error box where one failed
            to load would take a panel that says everything it needs to and make it look
            broken.
          */}
          {sliceState === 'ready' && slice ? (
            <MatrixPanel slice={slice} sourceViewable={sourceViewable} />
          ) : null}

          {/*
            And the one case worth saying out loud, to us only: the lookup ran and came
            back with nothing. That is either a citation with no cells behind it, or the
            grid route not being deployed on this API - and from the browser those look
            identical, so the note names both rather than picking one. A broker sees
            nothing at all, because for them there is nothing here to have gone wrong.

            `staffView`, for the same reason as the age badge: this names a source file and
            a route, which is a note to whoever can go and fix it.
          */}
          {staffView && sliceState === 'absent' ? (
            <div style={{ fontSize: 12, color: 'var(--faint)', lineHeight: 1.6 }}>
              No grid came back for this citation — either it points at something other
              than a matrix cell, or <code>/api/citation/{reference.id}/matrix</code> is
              not deployed on this API. See <code>fetchMatrixSlice</code> in api.ts.
            </div>
          ) : null}

          {!sourceViewable ? (
            /*
              The broker's panel ends with a plain statement of where the quote came from.
              No grey box, no "loading page", no empty frame: an absent element reads as a
              panel that was designed this way, while a disabled one reads as a feature
              somebody took away.
            */
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
              Quoted from {reference.lender_name}&apos;s own {DOCUMENT_NOUN[reference.document_kind]
                ?? 'document'}
              {reference.printed_label ? `, page ${reference.printed_label}` : ''}. Every
              quote is checked word for word against the stored page before an answer is
              written — nothing reaches this panel that the document does not say.
            </div>
          ) : (
          <div style={{
            // Was `flex: 1` when this box was the only thing under the quote. It now has
            // a grid and a stack of footnotes above it in the same scroller, so it takes
            // a height of its own instead of eating whatever is left: a page image that
            // collapsed to 40px under a tall grid would be unreadable, and one that
            // expanded to its natural height would push the footnotes a full page down.
            minHeight: 320, maxHeight: '62vh', overflow: 'auto', background: '#f1f5f9',
            borderRadius: 6, border: '1px solid var(--hair, #e2e8f0)',
          }}>
          {page === 'failed' ? (
            <div style={{ padding: '28px 20px', fontSize: 14, color: 'var(--muted)' }}>
              The source document could not be loaded, so the page cannot be shown here.
              The quote above is still what the guideline says — it is checked against the
              stored page text before any answer is written.
            </div>
          ) : (
            <div style={{ position: 'relative', lineHeight: 0 }}>
              <img
                src={src}
                alt={`Page ${reference.page_index} of ${reference.document_title}`}
                onLoad={() => setPage('ready')}
                onError={() => setPage('failed')}
                onClick={() => setZoom((z) => !z)}
                style={{
                  width: zoom ? '180%' : '100%', maxWidth: 'none', display: 'block',
                  cursor: zoom ? 'zoom-out' : 'zoom-in',
                }}
              />
              {page === 'ready' && hasBox ? (
                <div
                  ref={boxRef}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: pct(bbox![0]!), top: pct(bbox![1]!),
                    width: pct(bbox![2]! - bbox![0]!),
                    height: pct(bbox![3]! - bbox![1]!),
                    // Outlined rather than filled: a translucent wash over small figures
                    // is exactly where a 6 starts to look like an 8.
                    border: '2px solid #dc2626',
                    boxShadow: '0 0 0 4px rgba(220,38,38,.18)',
                    borderRadius: 2,
                    pointerEvents: 'none',
                  }}
                />
              ) : null}
              {page === 'loading' ? (
                <div style={{ padding: '28px 20px', fontSize: 13, color: 'var(--muted)' }}>
                  Loading page {reference.page_index}…
                </div>
              ) : null}
            </div>
          )}
          </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          {sourceViewable && page === 'ready' && !hasBox ? (
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              The exact position on the page is not recorded for this citation, so nothing
              is highlighted.
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
